import { useState } from 'react';

import { Box, Layers, ShieldAlert } from 'lucide-react';

import { useGetGames } from '@/api/getGames';
import { useMyPermissions } from '@/api/getMyPermissions';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Text } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { type Game } from '@/models/game';
import { WILDCARD, type MyRoleGrant } from '@/models/permission';
import { initials } from '@/utils/initials';

import { groupByResource } from './RoleSheet';
import { isAllSandboxes, scopeLabel } from '../models/member';

/** One Game's worth of the viewer's Grants — a tab. */
interface GameGroup {
  gameCode: string;
  label: string;
  grants: MyRoleGrant[];
}

/**
 * The viewer's Grants bucketed per Game, broadest first, then by the name actually
 * shown so the order matches the labels rather than the codes behind them.
 *
 * The label falls back to the raw game_code: /v1/games may be slow, may fail, or may
 * omit a Game the viewer holds a Grant on, and none of that should blank a tab.
 */
const groupByGame = (grants: MyRoleGrant[], games: Game[]): GameGroup[] => {
  const buckets = new Map<string, MyRoleGrant[]>();
  for (const grant of grants) {
    buckets.set(grant.game_code, [
      ...(buckets.get(grant.game_code) ?? []),
      grant,
    ]);
  }

  return [...buckets]
    .map(([gameCode, gameGrants]) => ({
      gameCode,
      label:
        gameCode === WILDCARD
          ? 'All Games'
          : (games.find((game) => game.game_code === gameCode)?.name ??
            gameCode),
      grants: gameGrants,
    }))
    .sort((a, b) => {
      if (a.gameCode === WILDCARD) return -1;
      if (b.gameCode === WILDCARD) return 1;
      return a.label.localeCompare(b.label);
    });
};

// Broadest first, then alphabetical: 'All Sandboxes' is the widest scope a Grant can
// carry, so it heads the list instead of sorting under 'A'.
const byScope = (a: MyRoleGrant, b: MyRoleGrant) => {
  if (isAllSandboxes(a) !== isAllSandboxes(b))
    return isAllSandboxes(a) ? -1 : 1;
  return a.sandbox_code.localeCompare(b.sandbox_code);
};

/**
 * One row per Role, holding every Sandbox it is granted on.
 *
 * A Role is defined once in the catalogue, so two Grants naming it carry the same
 * description and the same permissions: a row per Grant repeated the tallest cell of
 * the table once per Sandbox. `grant` is the Role's identity — any Grant of the group
 * answers for it — and `scopes` is what actually differs.
 *
 * game_code stays out of the key: it is already constant within a tab.
 */
const groupByRole = (grants: MyRoleGrant[]) => {
  const buckets = new Map<
    string,
    { grant: MyRoleGrant; scopes: MyRoleGrant[] }
  >();
  for (const grant of grants) {
    const bucket = buckets.get(grant.id);
    if (bucket) bucket.scopes.push(grant);
    else buckets.set(grant.id, { grant, scopes: [grant] });
  }

  // Insertion order is the API's order — nothing here invents a ranking of Roles.
  return [...buckets.values()].map(({ grant, scopes }) => ({
    grant,
    scopes: [...scopes].sort(byScope),
  }));
};

// Only the Sandboxes: the tab already says which Game this is, and the row already
// says which Role.
const SandboxCell = ({ grants }: { grants: MyRoleGrant[] }) => (
  <div className="flex flex-col gap-1">
    {grants.map((grant) => (
      <span
        key={grant.sandbox_code}
        className={cn(
          'flex items-center gap-1',
          isAllSandboxes(grant) && 'text-primary font-medium',
        )}
      >
        {isAllSandboxes(grant) ? <Layers size={12} /> : <Box size={12} />}
        {scopeLabel(grant)}
      </span>
    ))}
  </div>
);

// Wildcards are expanded into the concrete permissions they cover: an
// 'everything' label would hide what is actually granted.
const PermissionsCell = ({ grant }: { grant: MyRoleGrant }) => {
  const groups = groupByResource(grant.permissions);

  if (groups.length === 0)
    return <span className="text-muted-foreground">—</span>;

  return (
    <div className="flex flex-wrap gap-1">
      {groups.map(({ resource, label, actions }) => (
        <Badge key={resource} variant="outline" className="bg-muted">
          {label}: {actions.join(', ')}
        </Badge>
      ))}
    </div>
  );
};

const GrantsTable = ({ grants }: { grants: MyRoleGrant[] }) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead>Role</TableHead>
        <TableHead>Sandboxes</TableHead>
        <TableHead>Permissions</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {groupByRole(grants).map(({ grant, scopes }) => (
        <TableRow key={grant.id}>
          <TableCell className="align-top">
            <span className="font-mono font-medium">{grant.name}</span>
            <Text variant="muted">{grant.description}</Text>
          </TableCell>
          <TableCell className="align-top">
            <SandboxCell grants={scopes} />
          </TableCell>
          <TableCell className="align-top">
            <PermissionsCell grant={grant} />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const GrantsByGame = ({ grants }: { grants: MyRoleGrant[] }) => {
  const { data } = useGetGames();
  const games = data?.games ?? [];
  const groups = groupByGame(grants, games);

  // Deliberately ignores the Game being browsed. This dialog hangs off the app
  // header, is shared by every Game, and answers "what do I hold, everywhere" — so
  // it reads nothing from the route: opening it inside a Game must not change what
  // it lists nor which tab it lands on. Each tab shows the Grants of *its* Game.
  const [selected, setSelected] = useState(
    () => groups[0]?.gameCode ?? WILDCARD,
  );

  const active =
    groups.find((group) => group.gameCode === selected) ?? groups[0];

  // One Game is nothing to switch between: a tab bar there is chrome, not navigation.
  if (groups.length <= 1) return <GrantsTable grants={grants} />;

  return (
    <div className="flex flex-col gap-3">
      <Tabs value={active?.gameCode} onValueChange={setSelected}>
        <TabsList>
          {groups.map(({ gameCode: code, label, grants: gameGrants }) => (
            <TabsTrigger key={code} value={code} className="gap-2">
              {label}
              {/* Roles, not Grants: the table below is one row per Role. */}
              <Badge variant="secondary">
                {new Set(gameGrants.map((grant) => grant.id)).size}
              </Badge>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <GrantsTable grants={active?.grants ?? []} />
    </div>
  );
};

interface MyAccessDialogProps {
  /** The signed-in user's email — the only identity the session always carries. */
  username: string;
  /** From the Azure AD account when the session was opened; email is the fallback. */
  displayName?: string;
}

/**
 * The signed-in user's own identity and Grants: every Role they hold, on which
 * Game and Sandbox, and the permissions it confers. Answers "what am I allowed to
 * do" without needing access to the Access area.
 */
export const MyAccessDialog = ({
  username,
  displayName,
}: MyAccessDialogProps) => {
  const { data, isLoading, isError } = useMyPermissions();
  const grants = data?.roles ?? [];

  return (
    <>
      <DialogHeader className="flex-row items-center gap-4">
        <span className="bg-muted flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-medium uppercase">
          {/* An email is one "word", so initials() would yield a single letter. */}
          {displayName ? initials(displayName) : username.slice(0, 2)}
        </span>
        <div className="min-w-0">
          <DialogTitle>{displayName ?? username}</DialogTitle>
          <DialogDescription>{username}</DialogDescription>
        </div>
      </DialogHeader>
      <div className="max-h-[60vh] overflow-y-auto">
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : isError ? (
          <Alert variant="destructive">
            <ShieldAlert />
            <AlertTitle>Could not read your permissions</AlertTitle>
            <AlertDescription>
              Your Role Grants could not be loaded, so this list may be
              incomplete. Retry in a moment.
            </AlertDescription>
          </Alert>
        ) : grants.length === 0 ? (
          <Text variant="muted">
            You hold no Role Grant. Ask a Permission Manager of the Game you
            need access to.
          </Text>
        ) : (
          <GrantsByGame grants={grants} />
        )}
      </div>
    </>
  );
};
